import {
  subCompaniesApi,
  subCompanyCeilingDerivationApi,
  subCompanyPipelineAccessApi,
} from 'app/services/api';

import { catchAsyncActionError } from 'utils/action';
import { createNamedActions } from 'utils/namedActionTypesProxy';

import {
  buildChannelNameById,
  composePipelineAccessMatrix,
} from './subCompanyPipelineAccess.utils';

export const SUB_COMPANY_PIPELINE_ACCESS_ACTION_TYPE = createNamedActions(
  'subCompanyPipelineAccess',
  {
    GET_PIPELINE_ACCESS: 'GET_PIPELINE_ACCESS',
    GET_PIPELINE_ACCESS_REQUEST: 'GET_PIPELINE_ACCESS_REQUEST',
    GET_PIPELINE_ACCESS_FAILURE: 'GET_PIPELINE_ACCESS_FAILURE',
    RESET_STATE: 'RESET_STATE',
  },
);

// The matrix has no single endpoint — it is composed from three real, shipped contracts:
//   1. SkynetAPI exposed-pipelines BFF  -> the matrix ROWS + their effective channel scope,
//   2. the sub-company allowed-roles source -> the role COLUMNS,
//   3. per-pipeline Loans securityGroups -> the CELLS (provenance/tombstone/lock).
// Channel names come from the broker-safe ceiling-derivation read (never the Pricing catalog).
// The exposed-pipelines + securityGroups fetches are load-bearing (a failure surfaces the retry
// state); the channel-name derivation is best-effort and degrades to a scope-aware label.
const loadPipelineAccessMatrix = async subCompanyId => {
  const [allowedRolesResponse, exposedPipelinesResponse, channelNameById] = await Promise.all([
    subCompaniesApi.getSubCompanyAllowedRolesDetails({ subCompanyId }),
    subCompanyPipelineAccessApi.getExposedPipelines({ subCompanyId }),
    subCompanyCeilingDerivationApi
      .getSubCompanyCeilingDerivation({ subCompanyId })
      .then(({ data }) => buildChannelNameById(data?.sourceChannels))
      .catch(() => ({})),
  ]);

  const exposedPipelines = exposedPipelinesResponse.data?.pipelines || [];

  const securityGroupsEntries = await Promise.all(
    exposedPipelines.map(async pipeline => {
      const { data } = await subCompanyPipelineAccessApi.getPipelineSecurityGroups({
        pipelineId: pipeline.pipelineId,
      });

      return [pipeline.pipelineId, data || []];
    }),
  );

  return composePipelineAccessMatrix({
    allowedRoles: allowedRolesResponse.data || [],
    exposedPipelines,
    securityGroupsByPipelineId: Object.fromEntries(securityGroupsEntries),
    channelNameById,
  });
};

// Module-private: consumers load through getSubCompanyPipelineAccessWithStatusAction (which tracks
// the failed-load flag); the set/reset actions below use the bare version for their silent
// refetches.
const getSubCompanyPipelineAccessAction = catchAsyncActionError(
  ({ subCompanyId }) =>
    async dispatch => {
      const payload = await loadPipelineAccessMatrix(subCompanyId);

      return dispatch({
        type: SUB_COMPANY_PIPELINE_ACCESS_ACTION_TYPE.GET_PIPELINE_ACCESS,
        payload,
      });
    },
);

// pendingAction / rejectedAction feed the hasLoadFailed branch so the Pipeline Access tab can
// render an error + retry state instead of an indefinite spinner when the initial load fails. The
// silent post-write refetches keep using the base action — their rejection surfaces via the
// wrapping set/reset action's toast without flipping the whole matrix into the error state.
export const getSubCompanyPipelineAccessWithStatusAction = subCompanyId =>
  getSubCompanyPipelineAccessAction({
    subCompanyId,
    pendingAction: () => ({
      type: SUB_COMPANY_PIPELINE_ACCESS_ACTION_TYPE.GET_PIPELINE_ACCESS_REQUEST,
    }),
    rejectedAction: () => ({
      type: SUB_COMPANY_PIPELINE_ACCESS_ACTION_TYPE.GET_PIPELINE_ACCESS_FAILURE,
    }),
  });

// Turn one role on/off for one exposed pipeline, then re-read the matrix — the server owns the
// derived cell state (default/revoked/locked/broker-added), so a refetch is the source of truth
// rather than an optimistic local merge. isEnabled=true maps to the grant/un-hide upsert;
// isEnabled=false maps to the revoke DELETE (the backend picks tombstone-vs-delete by provenance).
// The inner refetch dispatches silently: its rejection propagates to THIS action's
// catchAsyncActionError wrapper, so a refetch failure surfaces as exactly one toast, not two.
export const setSubCompanyPipelineRoleAccessAction = catchAsyncActionError(
  ({ subCompanyId, pipelineId, roleId, isEnabled }) =>
    async dispatch => {
      const write = isEnabled
        ? subCompanyPipelineAccessApi.grantPipelineSecurityGroup({
            pipelineId,
            securityGroupId: roleId,
          })
        : subCompanyPipelineAccessApi.revokePipelineSecurityGroup({
            pipelineId,
            securityGroupId: roleId,
          });

      await write;

      return dispatch(getSubCompanyPipelineAccessAction({ subCompanyId, silent: true }));
    },
);

export const resetSubCompanyPipelineAccessAction = catchAsyncActionError(
  ({ subCompanyId, pipelineId }) =>
    async dispatch => {
      await subCompanyPipelineAccessApi.resetPipelineSecurityGroups({ pipelineId });

      return dispatch(getSubCompanyPipelineAccessAction({ subCompanyId, silent: true }));
    },
);
